import React from "react";

function formatNumber(value, format) {
  if (value === null || value === undefined || isNaN(value)) return "—";

  switch (format) {
    case "dollars":
      return `$${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
    case "percentage":
      return `${(value * 100).toFixed(1)}%`;
    case "number":
      return `${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      })}`;
    default:
      return value.toFixed(1);
  }
}

function getMetricLabel(metricValue, metrics) {
  if (!metricValue || !metrics || !Array.isArray(metrics)) {
    return metricValue || "Unknown";
  }

  let baseValue = metricValue;
  let suffix = "";

  if (metricValue.endsWith("_previous_year")) {
    baseValue = metricValue.replace("_previous_year", "");
    suffix = " (Prev Year)";
  }

  const metric = metrics.find((m) => m && m.value === baseValue);
  if (metric && metric.label) {
    return `${metric.label}${suffix}`;
  }
  return metricValue;
}

const MetricCards = ({
  selectedMetrics,
  metrics,
  summaryData,
  locationData,
  compareLocations,
  isDashboard,
  activeTab = "Daily",
  compactAxis,
}) => {
  const averageLabel = activeTab === "Weekly" ? "Weekly Average" : "Daily Average";
  const safeMetrics = metrics || [];
  const safeSelectedMetrics = selectedMetrics || [];
  const safeSummaryData = summaryData || [];
  const safeLocationData = locationData || [];
  const multipleComparisonMetrics = new Set(safeLocationData.map((row) => row.metric)).size > 1;

  const firstMetric = safeMetrics.find((m) => m && m.value === safeSelectedMetrics[0]);
  const formatType = firstMetric && firstMetric.format ? firstMetric.format : "number";

  // Compact mode: used by the ops assistant panel where space is tight.
  const gridCls = compactAxis
    ? "grid grid-cols-2 gap-2 p-2"
    : "grid sm:grid-cols-1 lg:grid-cols-2 grid-cols-3 gap-4 p-4";
  const cardPad = compactAxis ? "p-2" : "p-4";
  const cardLabelCls = compactAxis ? "text-xs font-medium mb-1" : "text-sm font-medium mb-2";
  const totalValueCls = compactAxis ? "text-base font-bold" : "text-2xl font-bold";
  const avgValueCls = compactAxis ? "text-sm font-semibold" : "text-lg font-semibold";
  const subLabelCls = compactAxis ? "text-[10px]" : "text-xs";
  const cardSpacing = compactAxis ? "space-y-1" : "space-y-2";

  const renderCards = () => {
    if (compareLocations && safeLocationData.length > 0) {
      // Show location comparison cards
      return (
        <div className={gridCls}>
          {safeLocationData.map((item, index) => {
            const itemFormat = safeMetrics.find((metric) => metric?.value === item.metric)?.format || formatType;
            return (
              <div
                key={index}
                className={`bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg ${cardPad}`}
              >
                <div className={`${cardLabelCls} text-violet-800`}>
                  {item.location || item.employee || item.role}
                  {multipleComparisonMetrics && ` — ${getMetricLabel(item.metric, safeMetrics)}`}
                </div>
                <div className={cardSpacing}>
                  <div>
                    <div className={`${totalValueCls} text-violet-900`}>
                      {formatNumber(item.total, itemFormat)}
                    </div>
                    <div className={`${subLabelCls} text-violet-600`}>Total</div>
                  </div>
                  {item.metric && item.metric !== "entrees_plh" &&
                   !item.metric.includes("splh") &&
                   itemFormat !== "percentage" && (
                    <div>
                      <div className={`${avgValueCls} text-violet-800`}>
                        {formatNumber(item.displayAverage ?? item.average, itemFormat)}
                      </div>
                      <div className={`${subLabelCls} text-violet-600`}>{averageLabel}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Show metric summary cards
    if (safeSummaryData.length > 0) {
      return (
        <div className={gridCls}>
          {safeSummaryData.map((metric, index) => {
            const metricInfo = safeMetrics.find((m) => m && m.value === metric.metric);
            const cardColor = index % 2 === 0 ? "violet" : "blue";

            return (
              <div
                key={index}
                className={`bg-gradient-to-r ${
                  cardColor === "violet"
                    ? "from-violet-50 to-purple-50 border-violet-200"
                    : "from-blue-50 to-cyan-50 border-blue-200"
                } border rounded-lg ${cardPad}`}
              >
                <div className={`${cardLabelCls} ${
                  cardColor === "violet" ? "text-violet-800" : "text-blue-800"
                }`}>
                  {getMetricLabel(metric.metric, safeMetrics)}
                </div>
                <div className={cardSpacing}>
                  <div>
                    <div className={`${totalValueCls} ${
                      cardColor === "violet" ? "text-violet-900" : "text-blue-900"
                    }`}>
                      {formatNumber(metric.total, metricInfo?.format || formatType)}
                    </div>
                    <div className={`${subLabelCls} ${
                      cardColor === "violet" ? "text-violet-600" : "text-blue-600"
                    }`}>
                      Total
                    </div>
                  </div>
                  {metric.metric && metric.metric !== "entrees_plh" &&
                   !metric.metric.includes("splh") &&
                   metricInfo?.format !== "percentage" && (
                    <div>
                      <div className={`${avgValueCls} ${
                        cardColor === "violet" ? "text-violet-800" : "text-blue-800"
                      }`}>
                        {formatNumber(metric.displayAverage ?? metric.average, metricInfo?.format || formatType)}
                      </div>
                      <div className={`${subLabelCls} ${
                        cardColor === "violet" ? "text-violet-600" : "text-blue-600"
                      }`}>
                        {averageLabel}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Fallback: No data message
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-gray-400 mb-2">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
            </svg>
          </div>
          <p className="text-gray-500">No metric data available</p>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full bg-white overflow-auto">
      {renderCards()}
    </div>
  );
};

export default MetricCards;
